import type { Reference } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import {
  type AcceptReceipt,
  type FieldDecisionStatus,
  isValidatedToolOp,
  type PersistedEditorOperation,
  type PersistedQuestionnaireAnswerOp,
  type PersistedValidatedToolOp,
  type StagedSuggestion,
} from '@seen/core/models/compositionNote'
import {
  type RependRestore,
  SuggestionStagingBasic,
} from '@seen/core/models/suggestionStagingBasic'
import { type QueryKey, useQueryClient } from '@tanstack/react-query'
import { cloneDeep } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { useCreateIfNoneExistsMutation } from '#src/hooks/useCreateIfNoneExistsMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

/**
 * Input for staging a fresh AI-suggestion batch into the note's per-practitioner
 * staging `Basic`. Built from the INPUT ops (NOT resolved decoration positions,
 * which don't survive y-prosemirror remaps). Carries inline editor ops AND
 * validated-tool suggestions — the one unified staging store for everything the
 * AI proposes on a note.
 */
export interface StageSuggestionInput {
  /** Decoration id (`${toolCallId}-${idx}`); becomes the persisted record id. */
  id: string
  /** The semantic op, verbatim. Persisted so hydration can re-resolve it. */
  op: PersistedEditorOperation
  /** Provenance — `toolCallId` is required so `clearByToolCall` lines up. */
  toolCallId: string
  threadId?: string
  /**
   * Run provenance: one id per user-initiated agent run. It is a STAMP, not a
   * selector — batches accumulate in the store whatever their `runId`, and a
   * later run never sweeps an earlier run's pendings. (The legacy
   * `CompositionNote.setSuggestions` did filter by it; nothing calls that any
   * more.) A pending record is replaced only by name: re-staged under the same
   * id, or superseded through `supersedes` by a revision that says so.
   */
  runId?: string
  /** Langfuse trace id for the agent run that produced this suggestion. */
  traceId?: string
  /** resolveOperation's label, for display when the live decoration is gone. */
  label?: string
  /**
   * The id of the record this one replaces, present only on a revision. Applied
   * in the SAME write that stages this record, so no reader can observe both as
   * pending — and the superseded record is kept as superseded training data
   * rather than deleted.
   */
  supersedes?: string
}

/* One questionnaire answer, named the way an undo has to name it: by the
 * questionnaire it belongs to and its key within that questionnaire, never by
 * the staging record it happened to be decided against. */
export interface QuestionnaireAnswerRef {
  questionnaireUrl: string
  atomKey: string
}

/* What a re-pend reports back: whether the staging write landed, and the undo
 * of exactly what it changed. A caller that reverses a clinical answer AFTER
 * re-pending uses `ok` to decide whether to reverse at all, and `revert` when
 * its own write then fails — either way the answer is never left out of the
 * plan with no card to accept it again. */
export interface RependOutcome {
  ok: boolean
  /* The answers that are actually undecided again — NOT simply the ones asked
   * for. `ok` says the staging write landed; it says nothing about whether any
   * atom came back, and a re-pend no-ops when no record holds the answer any
   * more or when there is no staging loaded to write to. A caller that reverses
   * a clinical answer must reverse only what appears here, or it takes a value
   * out of the plan with no card left to put it back. */
  repended: readonly QuestionnaireAnswerRef[]
  /* Put back the re-pend for these answers, or for all of them when none are
   * named. A caller whose own writes partly succeeded MUST name the ones that
   * did not: reverting an answer whose value has already left the plan would
   * take its card away too, which is the one outcome an undo may never reach. */
  revert: (only?: readonly QuestionnaireAnswerRef[]) => void
}

// Identity of one answer across questionnaires, for pairing a re-pend with its
// restore. An atom key alone is unique only within its own questionnaire.
export function answerRefKey(ref: QuestionnaireAnswerRef): string {
  return `${ref.questionnaireUrl}\u0000${ref.atomKey}`
}

/** A status flip projected from the editor's content-derived accepted-set. */
export interface SuggestionStatusFlip {
  id: string
  status: StagedSuggestion['status']
}

/* One atom's durable decision, as a batch write takes it: the staging record,
 * the decision key within it (an answer `linkId` for a validated tool, the
 * `questionnaireAnswerKey` atom key for a questionnaire), and the decision. */
export interface FieldDecisionEntry {
  id: string
  atomKey: string
  status: FieldDecisionStatus
  /* The clinician's answer, for an `edited` decision only. */
  value?: string
}

/* Said when a staging write does not land. Previously these failures were
 * swallowed whole: the decision quietly reverted on the next refetch while the
 * clinician believed it had stuck. */
export const SUGGESTION_WRITE_FAILED =
  'Could not save that decision. It has been put back — try again.'

/* Every cached `useSearchOne` result for a `Basic`. The staging record is
 * picked out of them by id below, so matching on the prefix rather than
 * rebuilding `useSearchOne`'s exact key costs nothing and cannot drift from
 * how that key is built. */
const STAGING_QUERY_PREFIX = ['Basic', 'searchOneExtended'] as const

export interface SuggestionPersistence {
  /**
   * True once the staging `Basic` search has SETTLED for an edited resource
   * (records readable). Gated on BOTH a resolved current-practitioner reference
   * AND the search settling, so hydration never latches an empty pass before the
   * author-scoped read resolves. False for surfaces with no resourceRef (no
   * staging to persist) and while the search is still pending.
   */
  isResourceLoaded: boolean
  /*
   * The pending records the clinician can actually reach: the record holding
   * each live slot (one questionnaire url, one validated tool), plus every
   * pending record with no slot. A second pending record for one slot renders
   * nowhere — `pendingQuestionnaireSuggestion` and its validated-tool twin
   * surface one each — so counting it would report work with no card behind it
   * and sweeping it would write answers the clinician never saw.
   */
  pendingSuggestions: StagedSuggestion[]
  /** True when the note carries ≥1 pending persisted suggestion. */
  hasPersistedPending: boolean
  /**
   * Synchronous snapshot of ALL persisted records (pending + decided), read
   * straight from the in-memory staging `Basic` model. The content-derived
   * reconciler walks these every docChanged tx to flip status by content
   * presence (design Q8), and the decided-sweep reads them to drop decorations
   * for remotely-decided records.
   */
  getAllSuggestions: () => StagedSuggestion[]
  /**
   * Stage a fresh batch into the staging `Basic` with run-scoped
   * clear-then-append (same-run batches accumulate; a new run's batch supersedes
   * prior runs' pendings without duplicating). One version-checked PATCH against
   * the per-practitioner Basic, or a conditional create on the first write so a
   * two-tab race makes exactly one Basic.
   */
  persistBatch: (batch: StageSuggestionInput[], generatedAt?: string) => void
  /**
   * Project content-derived accepted-set flips (and click-driven inline
   * rejects) into the staging `Basic`. One version-checked PATCH applying each
   * flip's `setSuggestionStatus`; concurrent writes re-apply the semantic
   * mutateFn on a fresh base (refetch-on-conflict), so flips merge rather than
   * clobber.
   */
  projectStatuses: (flips: SuggestionStatusFlip[]) => void
  /**
   * Immediate variant of `projectStatuses` for click-driven validated-tool
   * decisions. Both route to the same version-checked PATCH; kept as a distinct
   * name so callers stay unchanged.
   */
  projectStatusesNow: (flips: SuggestionStatusFlip[]) => void
  /**
   * Synchronous re-read of a suggestion's CURRENT status from the in-memory
   * staging `Basic` model. Returns undefined when the record is absent. The
   * accept handler calls this BEFORE inserting content (design Q5 guard);
   * hydration calls it so a re-paint never resurrects a just-decided suggestion.
   * Cross-tab convergence rides the Medplum cache sync (~1-2s refetch), so a
   * just-applied local flip is reflected after the mutation's cache update.
   */
  /**
   * Persist a DURABLE per-field accept/reject for a validated-tool suggestion,
   * keyed by answer `linkId`, so a partial decision survives reload. One
   * version-checked PATCH applying `setFieldDecision`; concurrent writes (e.g. a
   * decision on a sibling field of the same record) merge via
   * refetch-on-conflict rather than clobber. Writing `'pending'` clears the
   * decision. A no-op for a never-staged record (`createIfAbsent: false`).
   */
  projectFieldDecision: (
    id: string,
    linkId: string,
    status: FieldDecisionStatus
  ) => void
  /**
   * Batched `projectFieldDecision`: every entry is applied inside ONE
   * version-checked PATCH, the way `projectStatuses` already applies a list of
   * status flips. Bulk sweeps MUST use this — writes are serialized through a
   * single promise chain, so a per-atom call issued one PATCH per atom and a
   * two-dozen-answer sweep cleared its cards one at a time.
   *
   * The decisions are also applied OPTIMISTICALLY to the cached staging record,
   * so the surface reflects them before the write lands; a failed write puts
   * the cache back and toasts.
   */
  projectFieldDecisions: (entries: readonly FieldDecisionEntry[]) => void
  /**
   * Batched `projectFieldDecisions`, plus — when given — the record's derived
   * status flip, applied inside the SAME version-checked PATCH as the
   * decisions. A field decision and the record status a caller derives from it
   * (e.g. "every answer is now decided, so the record is settled") describe one
   * clinician action; writing them as two separate PATCHes lets either land
   * without the other, which can leave a settled record with undecided-looking
   * fields or a pending record with no visible work left. One write means one
   * failure: the decisions and the flip roll back together via the same
   * optimistic-cache rollback `projectFieldDecisions` already uses.
   * Resolves true once the write lands, false when it failed and was put back.
   */
  projectFieldDecisionsWithStatus: (
    entries: readonly FieldDecisionEntry[],
    statusFlip?: SuggestionStatusFlip
  ) => Promise<boolean>
  /**
   * Persist a suggestion's island-edited content (design §2.b), so a touched
   * suggestion survives an AI rerun and a later plain Accept uses the edit. One
   * version-checked PATCH applying `setEditedContent`; an empty/undefined
   * `content` clears the edit (Restore original). A no-op for a never-staged
   * record (`createIfAbsent: false`). NOTE: the write is async (patch + refetch),
   * so it is NOT synchronously visible via `getAllSuggestions` — Accept-with-edits
   * passes the live island content to the pipeline directly rather than reading
   * it back. Awaitable so the accept pipeline can persist the accepted edit
   * durably BEFORE the receipt (so a crash-resume re-materializes exactly it).
   */
  projectEditedContent: (
    id: string,
    content: string | undefined
  ) => Promise<void>
  getLatestStatus: (id: string) => StagedSuggestion['status'] | undefined
  /**
   * Accept guard: returns true if it's safe to insert (record is still
   * pending). Returns false (and toasts) when the record is already accepted or
   * rejected — preventing the double-insert that would duplicate clinical
   * content (design Q5). Staging is private per practitioner, so cross-RN
   * double-accept on the same staged record cannot occur.
   */
  guardAccept: (id: string) => boolean
  /** The accept receipt for a suggestion (R1 resume signal), or undefined. */
  getReceipt: (id: string) => AcceptReceipt | undefined
  /**
   * Persist the accept receipt (design §O.4 R1) for a suggestion BEFORE its side
   * effects run. Idempotent (a resume never clobbers a prior receipt). AWAITABLE:
   * the promise resolves once the version-checked write has settled, so the
   * pipeline can guarantee receipt-before-side-effects ordering. No-op for a
   * never-staged record.
   */
  recordAcceptReceipt: (id: string, nodeIds: string[]) => Promise<void>
  /**
   * Record a created FHIR resource ref on the receipt as it materializes (R1),
   * keyed by the stamp node id. Awaitable; no-op when the receipt is absent.
   */
  recordCreatedRef: (
    id: string,
    nodeId: string,
    reference: string
  ) => Promise<void>
  /**
   * Clear the receipt's recorded refs after compensation (R5). Awaitable; no-op
   * when the receipt is absent.
   */
  clearReceiptRefs: (id: string) => Promise<void>
  /**
   * The PENDING validated-tool suggestion for `toolName`, read from the staging
   * `Basic` (latest pending wins). The validated-tool node view reads this for
   * its banner/prefill instead of the FHIR note.
   */
  getPendingValidatedTool: (
    toolName: string
  ) => (StagedSuggestion & { op: PersistedValidatedToolOp }) | undefined
  /**
   * The PENDING questionnaire-answer suggestion for a canonical questionnaire
   * URL (latest pending wins). The care plan's questionnaire block reads this to
   * render its proposal banner.
   */
  getPendingQuestionnaireSuggestion: (
    questionnaireUrl: string
  ) => (StagedSuggestion & { op: PersistedQuestionnaireAnswerOp }) | undefined
  /*
   * Synchronous check that an undo can put a questionnaire answer back on the
   * surface. Asked BEFORE the compensating write, so reversing an accept never
   * takes text out of the clinical record with no card left to accept it again.
   */
  canRependQuestionnaireAnswer: (
    questionnaireUrl: string,
    atomKey: string
  ) => boolean
  /*
   * Return one questionnaire answer to undecided, on whichever record holds the
   * questionnaire's live slot — which is not necessarily the record the
   * decision was made against, since a later batch may have taken the slot over
   * since. Addressing the answer by KEY rather than by record id is what keeps
   * an undo working across AI runs.
   */
  rependQuestionnaireAnswer: (
    questionnaireUrl: string,
    atomKey: string
  ) => Promise<RependOutcome>
  /*
   * Batched `rependQuestionnaireAnswer`: every entry lands inside ONE
   * version-checked PATCH, the way `projectFieldDecisions` batches a sweep's
   * decisions. Taking a bulk accept back is one clinician action however many
   * answers it spans, and a call per answer would issue a PATCH per answer.
   */
  rependQuestionnaireAnswers: (
    entries: readonly QuestionnaireAnswerRef[]
  ) => Promise<RependOutcome>
}

/* ---- empty no-op persistence (surfaces with no resourceRef) ---- */
const EMPTY_PENDING: StagedSuggestion[] = []
const noopPersistence: SuggestionPersistence = {
  isResourceLoaded: false,
  pendingSuggestions: EMPTY_PENDING,
  hasPersistedPending: false,
  getAllSuggestions: () => EMPTY_PENDING,
  persistBatch: () => {},
  projectStatuses: () => {},
  projectStatusesNow: () => {},
  projectFieldDecision: () => {},
  projectFieldDecisions: () => {},
  projectFieldDecisionsWithStatus: () => Promise.resolve(true),
  projectEditedContent: () => Promise.resolve(),
  getLatestStatus: () => undefined,
  guardAccept: () => true,
  getReceipt: () => undefined,
  recordAcceptReceipt: () => Promise.resolve(),
  recordCreatedRef: () => Promise.resolve(),
  clearReceiptRefs: () => Promise.resolve(),
  getPendingValidatedTool: () => undefined,
  getPendingQuestionnaireSuggestion: () => undefined,
  canRependQuestionnaireAnswer: () => false,
  rependQuestionnaireAnswer: () =>
    Promise.resolve({ ok: true, repended: [], revert: () => {} }),
  rependQuestionnaireAnswers: () =>
    Promise.resolve({ ok: true, repended: [], revert: () => {} }),
}

/*
 * Build the persisted `StagedSuggestion` records from the staging inputs.
 * Stamps `provenance.stagedAt` at write time. Mirrors the prior store's record
 * shape so hydration / the reconciler read the same payload regardless of
 * backing store.
 */
interface Trace {
  traceId?: string
}

function recordsFromBatch(batch: StageSuggestionInput[]): StagedSuggestion[] {
  const stagedAt = new Date().toISOString()
  return batch.map((b) => {
    const trace: Trace = {}
    if (b.traceId) {
      trace.traceId = b.traceId
    }
    return {
      id: b.id,
      status: 'pending',
      op: b.op,
      provenance: {
        toolCallId: b.toolCallId,
        threadId: b.threadId,
        runId: b.runId,
        ...trace,
        stagedAt,
      },
      label: b.label,
    }
  })
}

/**
 * Owns the per-practitioner staging `Basic` read/write for an edited resource's
 * AI-suggestion staging so the collaborative-editor registry stays
 * storage-detail-free. Staging is PRIVATE to the current practitioner: the
 * read, the conditional-create, and the sign-delete are all scoped by
 * `Basic.author = Practitioner/{current}`, so another practitioner opening the
 * same resource sees their own empty staging until they run their own
 * generation.
 *
 * `resourceRef` is the edited resource's reference (e.g. `Composition/{id}` or
 * `CarePlan/{id}`); pass undefined for surfaces with no staging to persist —
 * every method then no-ops / returns empty.
 *
 * Writes are version-checked (no `skipVersionCheck`): a concurrent write to the
 * same Basic (one practitioner's two tabs) re-reads the latest copy and
 * re-applies the SEMANTIC mutateFn on it (`__experimental_refetchOnConflict`),
 * so the run-scoped stage / status flips MERGE rather than clobber.
 * @param resourceRef - The edited resource's reference, or undefined when there
 *   is no resource to persist staging for.
 * @returns The persistence facade for the resource's staging `Basic` (a shared
 *   no-op instance when `resourceRef` is undefined).
 */
export function useSuggestionPersistence(
  resourceRef: string | undefined
): SuggestionPersistence {
  const currentPractitioner = useCurrentPractitioner()
  const practitionerRef = currentPractitioner.reference.reference
  const queryClient = useQueryClient()

  /* Author-scoped read: the staging Basic for THIS resource owned by the CURRENT
   * practitioner, or null until the first write. Returns null (settled) when no
   * Basic exists; the search query is skipped (undefined) when there is no
   * resourceRef.
   *
   * `staleTime` keeps a same-session re-open (or the page-level banner mount and
   * the editor's own mount, which share this query key) from each forcing a cold
   * refetch — previously the default `staleTime: 0` made every mount re-fetch,
   * adding to the banner's first-paint latency (#10). Live-tab convergence is
   * unaffected: the staging write paths and the sign handler invalidate
   * `['Basic']` explicitly, and the Medplum subscription cache-sync invalidates
   * too — all of which refetch regardless of `staleTime`. So the decided-sweep
   * still sees remote decisions; this only suppresses redundant mount-time
   * refetches.
   *
   * `refetchOnWindowFocus: 'always'` covers the one gap a bare `staleTime` would
   * open: while a tab is backgrounded, `useSyncCache` unsubscribes from the
   * Medplum WebSocket, so a remote decide arriving during that pause is NOT
   * cache-invalidated; useSyncCache relies on TanStack's focus refetch to
   * reconverge, but a non-stale query wouldn't refetch on focus. Forcing the
   * focus refetch (`'always'`) makes a re-focused tab reconverge immediately
   * regardless of the staleTime window. */
  const search = useSearchOne(
    SuggestionStagingBasic,
    resourceRef && practitionerRef
      ? SuggestionStagingBasic.byResourceAndAuthor(resourceRef, practitionerRef)
      : undefined,
    { query: { staleTime: 30 * 1000, refetchOnWindowFocus: 'always' } }
  )
  const basic = search.data ?? undefined

  /* Latest search result in a ref so the write chain's re-seed and error
   * recovery read the CURRENT Basic, not one captured by a stale closure (an
   * old `enqueueWrite` identity could otherwise re-seed from a superseded
   * resource version). */
  const basicRef = useRef(basic)
  basicRef.current = basic

  const patchAsync = usePatchMutation<SuggestionStagingBasic>().mutateAsync
  const createIfNoneExistAsync =
    useCreateIfNoneExistsMutation<SuggestionStagingBasic>().mutateAsync

  /* The flag the propose-validated-tool tool is gated on server-side. Read here
   * too so turning it off retracts the whole feature rather than only stopping
   * new proposals: a record staged while it was on would otherwise keep its
   * review surface — controls, highlights, prefill — and keep counting in the
   * banner and the status bar, leaving the rollback half done. Filtering the
   * records out here withdraws all of that at once, at the seam every consumer
   * reads. The records themselves are untouched: nothing is settled, cleared or
   * deleted, so turning the flag back on restores the review as it was. */
  const validatedToolProposalsEnabled = useFeatureFlag(
    featureFlags.aiChatProposeValidatedTool
  )

  const pendingSuggestions = useMemo(() => {
    const pending = basic?.livePendingSuggestions ?? []
    return validatedToolProposalsEnabled
      ? pending
      : pending.filter((suggestion) => !isValidatedToolOp(suggestion.op))
  }, [basic, validatedToolProposalsEnabled])

  /* Serialize all writes through a single promise chain so concurrent writes in
   * one run (e.g. two `editEditor` batches, or `editEditor` + a validated-tool
   * stage that fire before the search refetches the just-created Basic into
   * `basic`) never each take the create-if-none-exist branch and silently drop
   * each other. Each link resolves to the latest persisted Basic; the next link
   * patches THAT, so the second batch is applied to the first's Basic rather
   * than re-running a create that resolves to the existing resource and drops
   * the batch. Seeded from the current search result so the first write after a
   * load patches the existing Basic. */
  const writeChainRef = useRef<Promise<SuggestionStagingBasic | undefined>>(
    Promise.resolve(undefined)
  )

  /* Scope the write chain to one resource: if the same editor instance is handed
   * a different resource without unmounting, reset the chain so a prior
   * resource's in-flight Basic can't be picked up by the next resource's first
   * write (which would land its batch on the wrong resource's staging). */
  // resourceRef is the reset key — the effect must rerun when the resource changes even though the callback never reads it.
  useEffect(() => {
    writeChainRef.current = Promise.resolve(undefined)
  }, [resourceRef])

  const enqueueWrite = useCallback(
    (
      apply: (b: SuggestionStagingBasic) => void,
      {
        createIfAbsent,
        onError,
      }: { createIfAbsent: boolean; onError?: (cause: unknown) => void }
    ): Promise<SuggestionStagingBasic | undefined> => {
      if (!resourceRef || !practitionerRef) {
        return Promise.resolve(undefined)
      }
      const author = practitionerRef
      const attempt = writeChainRef.current
        // Re-seed from the latest search result when the chain has no Basic yet
        // (idle), so a fresh load patches the existing Basic instead of creating.
        .then((chained) => chained ?? basicRef.current)
        .then(async (existing) => {
          if (existing) {
            return patchAsync({
              instance: existing,
              mutateFn: apply,
              patchOptions: { __experimental_refetchOnConflict: true },
            })
          }
          /* No staging Basic yet. A status flip on a never-staged resource is a
           * no-op (mirrors the prior store): don't materialize an empty Basic. */
          if (!createIfAbsent) {
            return undefined
          }
          /* First write for this (resource, practitioner): conditional-create so
           * a two-tab race resolves to ONE Basic. The query MUST include
           * `author` so a different practitioner generating on the same resource
           * creates a SEPARATE Basic. */
          const subject: Reference = { reference: resourceRef }
          const fresh = new SuggestionStagingBasic({
            subject,
            author: { reference: author },
          })
          apply(fresh)
          const upserted = await createIfNoneExistAsync({
            instance: fresh,
            query: SuggestionStagingBasic.byResourceAndAuthor(
              resourceRef,
              author
            ),
          })
          /* If the conditional-create resolved to a pre-existing Basic (a
           * concurrent tab created it first), our `apply` did NOT land on it —
           * patch the returned resource so this write isn't lost. When `upserted`
           * IS our fresh resource (we won the race), the payload already matches,
           * and the semantic mutateFn re-applied on it is idempotent. */
          return patchAsync({
            instance: upserted,
            mutateFn: apply,
            patchOptions: { __experimental_refetchOnConflict: true },
          })
        })
      /* A failed write must not poison the chain for subsequent writes; the
       * chain still resets to the latest known search result so the next write
       * can heal. What it no longer does is fail SILENTLY — the caller's
       * `onError` puts back whatever it reflected optimistically, and the
       * clinician is told the decision did not save instead of watching it
       * revert on the next refetch. */
      writeChainRef.current = attempt.catch((cause: unknown) => {
        onError?.(cause)
        toast.error(SUGGESTION_WRITE_FAILED)
        return basicRef.current
      })
      // Return the chain tail so callers that need write-ordering (the accept
      // pipeline awaiting the receipt before side effects, R1) can await it.
      return writeChainRef.current
    },
    [resourceRef, practitionerRef, patchAsync, createIfNoneExistAsync]
  )

  const persistBatch = useCallback(
    (batch: StageSuggestionInput[], generatedAt?: string) => {
      const records = recordsFromBatch(batch)
      void enqueueWrite(
        (b) => {
          /* Both halves in ONE write, and the supersession runs FIRST because
           * its result gates the staging. Atomicity is why they share a write:
           * split across two, a reload landing between them shows the clinician
           * the replacement and the proposal it replaces, side by side.
           *
           * A revision stages only if its target was still pending when this
           * write ran. The browser checked that before calling, but the
           * clinician may have decided the target in another tab since, and
           * `supersedeSuggestion` refuses a decided record. Staging the
           * replacement anyway would leave the decided original beside a
           * pending revision of it, re-proposing content already answered.
           * `records` is index-aligned with `batch` — `recordsFromBatch` maps
           * one to one. */
          const staged = records.filter((_record, i) => {
            const { supersedes, runId } = batch[i]
            if (supersedes === undefined) {
              return true
            }
            return b.supersedeSuggestion(supersedes, runId)
          })
          b.setSuggestions(staged, generatedAt)
        },
        { createIfAbsent: true }
      )
    },
    [enqueueWrite]
  )

  const applyFlips = useCallback(
    (flips: SuggestionStatusFlip[]) => {
      if (flips.length === 0) {
        return
      }
      void enqueueWrite(
        (b) => {
          for (const f of flips) {
            b.setSuggestionStatus(f.id, f.status)
          }
        },
        { createIfAbsent: false }
      )
    },
    [enqueueWrite]
  )

  /* Both project variants route to the same version-checked PATCH; the
   * validated-tool node view uses `projectStatusesNow` as a distinct name. */
  const projectStatuses = applyFlips
  const projectStatusesNow = applyFlips

  /* Reflect a batch of decisions in the cached staging record straight away, so
   * a decided card leaves the surface on the click rather than a round trip
   * later. Returns the undo, which the write's failure path runs.
   *
   * A CLONE is written, never the cached instance mutated in place: the model
   * holds its payload as a JSON string on the resource, and React only re-reads
   * `basic` when the identity changes. */
  const applyOptimisticDecisions = useCallback(
    (entries: readonly FieldDecisionEntry[]): (() => void) => {
      const staged = basicRef.current
      if (!staged) {
        return () => {}
      }
      /* The undo is the INVERSE of this call — each atom's prior status — not a
       * snapshot of the whole record. A snapshot would be correct only if
       * nothing else changed in between, and the interval it spans is a failing
       * network write: the clinician goes on deciding other answers while it
       * retries, and restoring the snapshot would silently erase every decision
       * they made in the meantime. */
      const restores: [QueryKey, FieldDecisionEntry[]][] = []
      const cached = queryClient.getQueriesData<SuggestionStagingBasic | null>({
        queryKey: STAGING_QUERY_PREFIX,
      })
      for (const [key, value] of cached) {
        if (
          !(value instanceof SuggestionStagingBasic) ||
          value.id !== staged.id
        ) {
          continue
        }
        const next = SuggestionStagingBasic.fromFhirResource(
          cloneDeep(value.toFhirResource())
        )
        const priors: FieldDecisionEntry[] = []
        for (const entry of entries) {
          priors.push({
            id: entry.id,
            atomKey: entry.atomKey,
            status: value.getFieldDecision(entry.id, entry.atomKey),
            value: value.getFieldEdit(entry.id, entry.atomKey),
          })
          next.setFieldDecision(
            entry.id,
            entry.atomKey,
            entry.status,
            entry.value
          )
        }
        restores.push([key, priors])
        queryClient.setQueryData(key, next)
      }
      return () => {
        for (const [key, priors] of restores) {
          /* Re-read rather than close over the record: the undo is applied to
           * whatever is cached NOW, so decisions made while the write was in
           * flight survive it. */
          const current = queryClient.getQueryData<
            SuggestionStagingBasic | null | undefined
          >(key)
          if (!(current instanceof SuggestionStagingBasic)) {
            continue
          }
          const reverted = SuggestionStagingBasic.fromFhirResource(
            cloneDeep(current.toFhirResource())
          )
          for (const prior of priors) {
            reverted.setFieldDecision(
              prior.id,
              prior.atomKey,
              prior.status,
              prior.value
            )
          }
          queryClient.setQueryData(key, reverted)
        }
      }
    },
    [queryClient]
  )

  const projectFieldDecisions = useCallback(
    (entries: readonly FieldDecisionEntry[]) => {
      if (entries.length === 0) {
        return
      }
      const rollback = applyOptimisticDecisions(entries)
      void enqueueWrite(
        (b) => {
          for (const entry of entries) {
            b.setFieldDecision(
              entry.id,
              entry.atomKey,
              entry.status,
              entry.value
            )
          }
        },
        { createIfAbsent: false, onError: rollback }
      )
    },
    [enqueueWrite, applyOptimisticDecisions]
  )

  const projectFieldDecision = useCallback(
    (id: string, linkId: string, status: FieldDecisionStatus) =>
      projectFieldDecisions([{ id, atomKey: linkId, status }]),
    [projectFieldDecisions]
  )

  /* The decisions and the derived status ride ONE version-checked PATCH, so a
   * failure cannot leave them disagreeing — a settled status persisting with no
   * decisions behind it (hiding pending work) or saved decisions persisting
   * under a still-pending status (recreating the stuck-record banner this
   * batching exists to close). */
  const projectFieldDecisionsWithStatus = useCallback(
    (
      entries: readonly FieldDecisionEntry[],
      statusFlip?: SuggestionStatusFlip
    ): Promise<boolean> => {
      if (entries.length === 0) {
        return Promise.resolve(true)
      }
      const rollback = applyOptimisticDecisions(entries)
      let landed = true
      return enqueueWrite(
        (b) => {
          for (const entry of entries) {
            b.setFieldDecision(
              entry.id,
              entry.atomKey,
              entry.status,
              entry.value
            )
          }
          if (statusFlip) {
            b.setSuggestionStatus(statusFlip.id, statusFlip.status)
          }
        },
        {
          createIfAbsent: false,
          onError: () => {
            landed = false
            rollback()
          },
        }
      ).then(() => landed)
    },
    [enqueueWrite, applyOptimisticDecisions]
  )

  const projectEditedContent = useCallback(
    (id: string, content: string | undefined): Promise<void> =>
      enqueueWrite((b) => b.setEditedContent(id, content), {
        createIfAbsent: false,
      }).then(() => {}),
    [enqueueWrite]
  )

  const getAllSuggestions = useCallback((): StagedSuggestion[] => {
    if (!resourceRef) {
      return []
    }
    const all = basic?.suggestions ?? []
    return validatedToolProposalsEnabled
      ? all
      : all.filter((suggestion) => !isValidatedToolOp(suggestion.op))
  }, [resourceRef, basic, validatedToolProposalsEnabled])

  const getLatestStatus = useCallback(
    (id: string): StagedSuggestion['status'] | undefined =>
      resourceRef ? basic?.getSuggestion(id)?.status : undefined,
    [resourceRef, basic]
  )

  const guardAccept = useCallback(
    (id: string): boolean => {
      const status = getLatestStatus(id)
      /* Block the insert for any record that is no longer pending. `accepted`
       * is the classic double-insert guard (design Q5); `rejected` matters too
       * — a rejected suggestion whose decoration hasn't been swept yet must not
       * be acceptable (it would insert superseded clinical content). An
       * `undefined` status means no persisted record (no resourceRef, or never
       * staged) → allow the editor-only accept, as before. */
      if (status === 'accepted') {
        toast.info('That suggestion was already accepted.')
        return false
      }
      if (status === 'rejected') {
        toast.info('That suggestion was already rejected.')
        return false
      }
      return true
    },
    [getLatestStatus]
  )

  const getReceipt = useCallback(
    (id: string): AcceptReceipt | undefined =>
      resourceRef ? basic?.getReceipt(id) : undefined,
    [resourceRef, basic]
  )

  const recordAcceptReceipt = useCallback(
    (id: string, nodeIds: string[]): Promise<void> =>
      enqueueWrite((b) => b.recordAcceptReceipt(id, nodeIds), {
        createIfAbsent: false,
      }).then(() => {}),
    [enqueueWrite]
  )

  const recordCreatedRef = useCallback(
    (id: string, nodeId: string, reference: string): Promise<void> =>
      enqueueWrite((b) => b.recordCreatedRef(id, nodeId, reference), {
        createIfAbsent: false,
      }).then(() => {}),
    [enqueueWrite]
  )

  const clearReceiptRefs = useCallback(
    (id: string): Promise<void> =>
      enqueueWrite((b) => b.clearReceiptRefs(id), {
        createIfAbsent: false,
      }).then(() => {}),
    [enqueueWrite]
  )

  const getPendingValidatedTool = useCallback(
    (toolName: string) =>
      resourceRef && validatedToolProposalsEnabled
        ? basic?.pendingValidatedToolSuggestion(toolName)
        : undefined,
    [resourceRef, basic, validatedToolProposalsEnabled]
  )

  const getPendingQuestionnaireSuggestion = useCallback(
    (questionnaireUrl: string) =>
      resourceRef
        ? basic?.pendingQuestionnaireSuggestion(questionnaireUrl)
        : undefined,
    [resourceRef, basic]
  )

  const canRependQuestionnaireAnswer = useCallback(
    (questionnaireUrl: string, atomKey: string): boolean =>
      resourceRef
        ? (basic?.canRependQuestionnaireAnswer(questionnaireUrl, atomKey) ??
          false)
        : false,
    [resourceRef, basic]
  )

  /* Apply a staging mutation to every cached copy of the record straight away,
   * so the surface reflects it on the click rather than a round trip later. A
   * CLONE is written, never the cached instance mutated in place: the model
   * holds its payload as a JSON string on the resource, and React only re-reads
   * `basic` when the identity changes.
   *
   * Undoing one of these is `restoreRepend` applied the same way, never a
   * snapshot put back — the window a failing write spans is one the clinician
   * goes on deciding through, and a snapshot would erase what they did in it. */
  const applyToCache = useCallback(
    (mutate: (staged: SuggestionStagingBasic) => void): void => {
      const staged = basicRef.current
      if (!staged) {
        return
      }
      const cached = queryClient.getQueriesData<SuggestionStagingBasic | null>({
        queryKey: STAGING_QUERY_PREFIX,
      })
      for (const [key, value] of cached) {
        if (
          !(value instanceof SuggestionStagingBasic) ||
          value.id !== staged.id
        ) {
          continue
        }
        const next = SuggestionStagingBasic.fromFhirResource(
          cloneDeep(value.toFhirResource())
        )
        mutate(next)
        queryClient.setQueryData(key, next)
      }
    },
    [queryClient]
  )

  const rependQuestionnaireAnswers = useCallback(
    (entries: readonly QuestionnaireAnswerRef[]): Promise<RependOutcome> => {
      const noop: RependOutcome = { ok: true, repended: [], revert: () => {} }
      if (entries.length === 0 || !basicRef.current) {
        return Promise.resolve(noop)
      }
      /* The cache is updated FIRST so the card is back before the write lands.
       * Without it an undo reads as having done nothing until the round trip
       * finishes — and a redo asked in between is refused, because the guard it
       * reads still says the atom is decided. */
      const restores = new Map<string, RependRestore>()
      applyToCache((staged) => {
        for (const entry of entries) {
          const restore = staged.rependQuestionnaireAnswer(
            entry.questionnaireUrl,
            entry.atomKey
          )
          if (!restore) {
            continue
          }
          /* The status this batch FOUND the record in, carried onto every atom
           * of it: by the second re-pend the record already reads pending
           * because of the first, and a full restore has to return the one it
           * started from. */
          const first = [...restores.values()].find(
            (held) => held.recordId === restore.recordId
          )
          restores.set(
            answerRefKey(entry),
            first
              ? { ...restore, status: first.status, decidedAt: first.decidedAt }
              : restore
          )
        }
      })
      const selected = (
        only: readonly QuestionnaireAnswerRef[] | undefined
      ): RependRestore[] =>
        only === undefined
          ? [...restores.values()]
          : only.flatMap((ref) => {
              const restore = restores.get(answerRefKey(ref))
              return restore ? [restore] : []
            })
      const restoreCache = (chosen: RependRestore[]) =>
        applyToCache((staged) => {
          for (const restore of chosen) {
            staged.restoreRepend(restore)
          }
        })
      let ok = true
      return enqueueWrite(
        (b) => {
          for (const entry of entries) {
            b.rependQuestionnaireAnswer(entry.questionnaireUrl, entry.atomKey)
          }
        },
        {
          createIfAbsent: false,
          onError: () => {
            ok = false
            restoreCache(selected(undefined))
          },
        }
      ).then(() => ({
        ok,
        /* Read from the restores the cache write actually produced, so an atom
         * no record holds any more is reported as what it is: not re-pended. */
        repended: entries.filter((entry) => restores.has(answerRefKey(entry))),
        /* The purpose-built inverse, for a caller whose own write then failed:
         * it puts back exactly the atoms this call touched, so the record stops
         * offering a card for a value that is still sitting in the answer. */
        revert: (only) => {
          const chosen = selected(only)
          if (chosen.length === 0) {
            return
          }
          restoreCache(chosen)
          void enqueueWrite(
            (b) => {
              for (const restore of chosen) {
                b.restoreRepend(restore)
              }
            },
            { createIfAbsent: false }
          )
        },
      }))
    },
    [enqueueWrite, applyToCache]
  )

  const rependQuestionnaireAnswer = useCallback(
    (questionnaireUrl: string, atomKey: string) =>
      rependQuestionnaireAnswers([{ questionnaireUrl, atomKey }]),
    [rependQuestionnaireAnswers]
  )

  /* Loaded once BOTH a current-practitioner reference is resolved AND the
   * author-scoped search has resolved successfully. An editor latches
   * hydration (and the deferred-reveal decision) on this, so it must not be
   * true while the read is in flight — and an errored read must not count
   * either: the latch would be consumed against transiently-empty data, and a
   * later successful refetch (window focus after network recovery) would never
   * re-fire it, leaving persisted suggestions invisible until a full reload. */
  const isResourceLoaded =
    resourceRef !== undefined &&
    practitionerRef !== undefined &&
    search.isSuccess

  return useMemo(
    () =>
      resourceRef === undefined
        ? noopPersistence
        : {
            isResourceLoaded,
            pendingSuggestions,
            hasPersistedPending: pendingSuggestions.length > 0,
            getAllSuggestions,
            persistBatch,
            projectStatuses,
            projectStatusesNow,
            projectFieldDecision,
            projectFieldDecisions,
            projectFieldDecisionsWithStatus,
            projectEditedContent,
            getLatestStatus,
            guardAccept,
            getReceipt,
            recordAcceptReceipt,
            recordCreatedRef,
            clearReceiptRefs,
            getPendingValidatedTool,
            getPendingQuestionnaireSuggestion,
            canRependQuestionnaireAnswer,
            rependQuestionnaireAnswer,
            rependQuestionnaireAnswers,
          },
    [
      resourceRef,
      isResourceLoaded,
      pendingSuggestions,
      getAllSuggestions,
      persistBatch,
      projectStatuses,
      projectStatusesNow,
      projectFieldDecision,
      projectFieldDecisions,
      projectFieldDecisionsWithStatus,
      projectEditedContent,
      getLatestStatus,
      guardAccept,
      getReceipt,
      recordAcceptReceipt,
      recordCreatedRef,
      clearReceiptRefs,
      getPendingValidatedTool,
      getPendingQuestionnaireSuggestion,
      canRependQuestionnaireAnswer,
      rependQuestionnaireAnswer,
      rependQuestionnaireAnswers,
    ]
  )
}
